import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, X } from "lucide-react";
import { apiError, recruitersAdminApi } from "../api";
import { dayLong } from "../format";
import { Empty, Field, Modal } from "../ui";
import type { Proposal } from "../types";
import { LoadingBlock, ErrorBlock } from "../../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { Can } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

/**
 * Productores que proponen los reclutadores. Al aceptar, se registran en el
 * CRM de Seguimiento y la atribución corre desde hoy (B8). Si ya estaban en el
 * pipeline, se rechaza con nota para informarle con transparencia.
 */
export function ProposalsPane() {
  const q = useQuery({ queryKey: ["recruiters", "proposals"], queryFn: recruitersAdminApi.proposals });
  const [deciding, setDeciding] = useState<{ p: Proposal; action: "accept" | "reject" } | null>(null);

  if (q.isLoading) return <LoadingBlock label="Cargando propuestas…" />;
  if (q.isError) return <ErrorBlock onRetry={() => q.refetch()} />;
  const rows = q.data?.proposals ?? [];
  if (rows.length === 0) return <Empty>No hay propuestas pendientes.</Empty>;

  return (
    <>
      <ul className="grid gap-3">
        {rows.map((p) => (
          <li key={p.id} className="nx-card p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-fg">{p.name}</div>
                <div className="text-xs text-fg-muted">
                  Propuesto por {p.recruiterName} · {dayLong(p.proposedAt)}
                </div>
              </div>
              <Can permission={PERMISSIONS.RECRUITERS_MANAGE}>
                <div className="flex gap-2">
                  <button type="button" className="nx-btn-secondary" onClick={() => setDeciding({ p, action: "reject" })}>
                    <X size={14} /> Rechazar
                  </button>
                  <button type="button" className="nx-btn-primary" onClick={() => setDeciding({ p, action: "accept" })}>
                    <Check size={14} /> Aceptar
                  </button>
                </div>
              </Can>
            </div>
            <div className="text-sm text-fg-muted space-y-1">
              {p.contactEmail && <div>Correo: <span className="text-fg">{p.contactEmail}</span></div>}
              {p.phone && <div>Teléfono: <span className="text-fg">{p.phone}</span></div>}
              {p.note && <p className="text-fg">“{p.note}”</p>}
            </div>
            {p.crmMatches.length > 0 && (
              <div className="rounded-md border border-warning/40 bg-warning-soft p-3 text-sm">
                <div className="font-medium text-warning">Ya hay prospectos parecidos en el CRM</div>
                <ul className="mt-1 space-y-0.5 text-fg">
                  {p.crmMatches.map((m) => (
                    <li key={m.id}>
                      {m.name} {m.contactEmail ? `· ${m.contactEmail}` : ""} · registrado el {dayLong(m.createdOn)}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-fg-muted">
                  Si ya estaba en el pipeline antes de la propuesta, recházala con una nota (B8).
                </p>
              </div>
            )}
          </li>
        ))}
      </ul>
      {deciding && <DecideModal proposal={deciding.p} action={deciding.action} onClose={() => setDeciding(null)} />}
    </>
  );
}

function DecideModal({ proposal, action, onClose }: { proposal: Proposal; action: "accept" | "reject"; onClose: () => void }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [linkTo, setLinkTo] = useState<number | "">("");
  const mut = useMutation({
    mutationFn: () =>
      recruitersAdminApi.decideProducer(proposal.id, {
        action,
        note: note.trim() || undefined,
        prospectId: action === "accept" && linkTo ? Number(linkTo) : undefined,
      }),
    onSuccess: () => {
      push({
        kind: "success",
        message: action === "accept" ? `${proposal.name} quedó en la cartera de ${proposal.recruiterName}.` : "Propuesta rechazada.",
      });
      qc.invalidateQueries({ queryKey: ["recruiters"] });
      onClose();
    },
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });
  const valid = action === "accept" || note.trim().length > 0;
  return (
    <Modal
      title={action === "accept" ? `Aceptar a ${proposal.name}` : `Rechazar a ${proposal.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="nx-btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className={action === "accept" ? "nx-btn-primary" : "nx-btn-danger"}
            disabled={!valid || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending && <Loader2 size={14} className="animate-spin" />}
            {action === "accept" ? "Aceptar y registrar" : "Rechazar"}
          </button>
        </>
      }
    >
      {action === "accept" ? (
        <>
          <p className="text-sm text-fg-muted">
            Se registra en el CRM como prospecto productor y queda asignado a {proposal.recruiterName} desde hoy. Tiene 90
            días para mostrar actividad antes de caducar.
          </p>
          {proposal.crmMatches.length > 0 && (
            <Field label="¿Es alguno de estos prospectos?" hint="Si lo eliges, se enlaza en vez de crear uno nuevo.">
              <select className="nx-input" value={linkTo} onChange={(e) => setLinkTo(e.target.value ? Number(e.target.value) : "")}>
                <option value="">No, crear un prospecto nuevo</option>
                {proposal.crmMatches.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} · {dayLong(m.createdOn)}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Nota (opcional)">
            <textarea className="nx-input min-h-[64px]" value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </>
      ) : (
        <Field label="¿Por qué?" required hint="El reclutador lo ve. Ej.: ya estaba en nuestro pipeline desde agosto.">
          <textarea className="nx-input min-h-[80px]" value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
        </Field>
      )}
    </Modal>
  );
}
